import { domainEventBus, RoiDeletionRequestedEvent } from '~/domain/events';
import type { RoiId } from '~/domain/changes/ids';

/**
 * Rolls back ROIs an apply-style operation CREATED before it failed, via
 * the same tracked-deletion event the delete button uses — shared by
 * `useCouchModelApplication` and `useBodyContourApplication` so the two
 * failure paths cannot drift (a rollback one caller logs and the other
 * skips silently is exactly the divergence this prevents).
 *
 * `createdRoiIds` is DRAINED (spliced empty) once the rollback is
 * attempted, so a second call — a re-entrant failure path, or a caller's
 * own catch running after an inner one — cannot publish a second deletion
 * for the same ROI. Two deliberate refusals leave the list intact:
 *
 * - An empty list is a no-op: most failures happen before anything was
 *   created, and logging "left behind: (nothing)" would train readers to
 *   ignore the real message.
 * - A missing `structureSetUid` skips the drain too — bailing out AFTER
 *   the splice would throw the rollback list away along with any second
 *   chance at it. It is LOGGED, never silent: orphaned AUTOMATIC stub
 *   ROIs with no trace of why they were left behind would send a support
 *   engineer looking in the wrong place. Structure ids only, no PHI.
 *
 * Never throws: the original failure is what the user needs surfaced; a
 * rollback failure only gets logged, per ROI, and the remaining ROIs are
 * still attempted.
 */
export async function rollbackCreatedRois(
    structureSetUid: string | undefined,
    createdRoiIds: RoiId[]
): Promise<void> {
    if (createdRoiIds.length === 0) {
        return;
    }
    if (!structureSetUid) {
        console.error(
            'ROI rollback skipped: the structure set has no SOP Instance ' +
                `UID. Stub ROIs left behind: ${createdRoiIds.join(', ')}`
        );
        return;
    }
    const toRollback = createdRoiIds.splice(0, createdRoiIds.length);
    for (const roiId of toRollback) {
        try {
            await domainEventBus.publish(
                new RoiDeletionRequestedEvent(structureSetUid, roiId, 'user')
            );
        } catch (rollbackError) {
            console.error(
                `Failed to roll back created ROI ${roiId} after a failed apply:`,
                rollbackError
            );
        }
    }
}
